import React, { useEffect, useState } from 'react';
import { Users, Coins, ArrowRight, Plus, Sparkles, Clock } from 'lucide-react';
import type { LobbyRoom } from '../../shared/protocol';
import { realtime } from '../lib/realtime';
import { BRAND } from '../brand';
import { useCountdown, formatCountdown, localClockOf } from './StartTimePicker';

/**
 * Partidas públicas: las que esperan gente y las que ya están en juego.
 *
 * La lista llega empujada por el servidor, no se pide cada tantos segundos:
 * una sala que se llena o arranca cambia al momento, que es justo lo que
 * evita pulsar "Unirse" en una partida que ya no admite a nadie.
 *
 * Vive en un hook y no dentro de la lista para que la pestaña pueda mostrar
 * cuántas hay aunque el panel visible sea otro.
 */
export function useLobbyRooms(): LobbyRoom[] | null {
  const [rooms, setRooms] = useState<LobbyRoom[] | null>(null);

  useEffect(() => {
    const off = realtime.on((msg) => {
      if (msg.t === 'lobby:list') setRooms(msg.rooms);
    });
    // Suscribirse cada vez que la conexión queda lista: tras una caída el
    // servidor ya no recuerda que estábamos mirando.
    const offStatus = realtime.onStatus((status) => {
      if (status === 'online') realtime.send({ t: 'lobby:subscribe' });
    });
    return () => {
      off();
      offStatus();
      if (realtime.status === 'online') realtime.send({ t: 'lobby:unsubscribe' });
    };
  }, []);

  return rooms;
}

interface Props {
  rooms: LobbyRoom[] | null;
  onJoin: (pin: string) => void;
  /** Abre «Crear partida»: una sala que dirige el servidor. */
  onCreateMatch: () => void;
}

/**
 * Sala de espera: la lista de partidas públicas, sin marco propio.
 *
 * Arriba, siempre a mano, la forma de abrir una partida nueva. Las que ya
 * están en juego siguen en la lista, marcadas como «En curso», para que se
 * vea que hay gente jugando aunque no se pueda (o no convenga) entrar.
 */
export const OpenRooms: React.FC<Props> = ({ rooms, onJoin, onCreateMatch }) => {
  const header = (
    <div className="flex items-center gap-3 justify-between p-3 sm:px-4 border-b border-ink-800">
      <p className="text-ink-400 text-sm min-w-0">
        {rooms === null
          ? 'Buscando partidas…'
          : rooms.length === 0
            ? 'No hay partidas abiertas ahora mismo.'
            : `${rooms.filter((r) => r.status !== 'playing').length} esperando · ${
                rooms.filter((r) => r.status === 'playing').length
              } en curso`}
      </p>
      <button
        onClick={onCreateMatch}
        className="flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg font-semibold text-sm shrink-0
                   bg-sky-600 hover:bg-sky-500 text-white transition-colors"
      >
        <Plus className="w-4 h-4" />
        Crear partida
      </button>
    </div>
  );

  if (rooms === null) {
    return (
      <>
        {header}
        <div className="h-24 shimmer rounded-lg m-3" />
      </>
    );
  }

  if (rooms.length === 0) {
    return (
      <>
        {header}
        <p className="p-6 text-ink-500 text-sm text-center">
          Crea una: elige una trivia, sortéala o deja que la IA escriba una, y la sala aparecerá
          aquí para que entre quien quiera.
        </p>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="divide-y divide-ink-800">
        {rooms.map((room) => (
          <RoomRow key={room.pin} room={room} onJoin={onJoin} />
        ))}
      </div>
    </>
  );
};

const RoomRow: React.FC<{ room: LobbyRoom; onJoin: (pin: string) => void }> = ({ room, onJoin }) => {
  const free = room.maxPlayers - room.players;
  const playing = room.status === 'playing';
  const left = useCountdown(room.status === 'waiting' ? room.startAt : null);

  return (
    <div
      className={`flex items-center gap-3 p-3 sm:px-4 transition-colors ${
        playing ? 'bg-ink-900/40' : 'hover:bg-ink-850/60'
      }`}
    >
      <span className={`font-emoji text-3xl leading-none shrink-0 ${playing ? 'opacity-70' : ''}`} aria-hidden="true">
        {room.coverEmoji}
      </span>

      <div className="min-w-0 flex-1">
        {/* En el teléfono la etiqueta va en su propia línea: al lado del
            título lo dejaba en dos letras. */}
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-semibold text-ink-50 truncate">{room.quizTitle}</p>
          <span className="hidden sm:contents">
            <StatusChip room={room} countdown={left} />
          </span>
        </div>
        <div className="sm:hidden empty:hidden flex my-0.5">
          <StatusChip room={room} countdown={left} />
        </div>
        <p className="text-ink-500 text-xs truncate">
          {room.serverHosted ? `Creada por ${room.hostName}` : room.hostName}
          {room.status !== 'preparing' && (
            <>
              {' · '}
              {room.questionCount} {room.questionCount === 1 ? 'pregunta' : 'preguntas'}
            </>
          )}
          {' · '}
          {room.gameMode === 'teams' ? 'Por equipos' : 'Individual'}
          {room.serverHosted && ' · Sin anfitrión'}
          {room.monitored && ' · Supervisada'}
          {room.hostPlays && ' · Juega el anfitrión'}
        </p>
      </div>

      {room.prizePool > 0 && (
        <span
          className="hidden sm:inline-flex chip border-ember-500 bg-ember-500/15 text-ember-300 shrink-0"
          title={`Bote de ${room.prizePool} ${BRAND.currencyName}`}
        >
          <Coins className="w-3.5 h-3.5" />
          {room.prizePool.toLocaleString('es')}
        </span>
      )}

      <span
        className="flex items-center gap-1 text-sm tabular-nums text-ink-300 shrink-0"
        title={`${free} ${free === 1 ? 'plaza libre' : 'plazas libres'}`}
      >
        <Users className="w-4 h-4 text-sky-400" />
        {room.players}/{room.maxPlayers}
      </span>

      <button
        onClick={() => onJoin(room.pin)}
        disabled={!room.joinable}
        title={room.joinable ? undefined : 'Esta partida ya empezó y no admite entradas tarde.'}
        className={`flex items-center gap-1 px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm shrink-0 transition-colors
                    disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed ${
                      playing
                        ? 'bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100'
                        : 'bg-ember-500 hover:bg-ember-400 text-on-accent'
                    }`}
        aria-label={`${playing ? 'Entrar a la partida en curso' : 'Unirse a'} ${room.quizTitle}`}
      >
        {playing ? (room.joinable ? 'Entrar' : 'En curso') : 'Unirse'}
        {room.joinable && <ArrowRight className="w-4 h-4 hidden sm:block" />}
      </button>
    </div>
  );
};

const StatusChip: React.FC<{ room: LobbyRoom; countdown: number | null }> = ({ room, countdown }) => {
  if (room.status === 'playing') {
    return (
      <span className="chip shrink-0 border-success-600 bg-success-500/10 text-success-400 text-[10px]">
        <span className="w-1.5 h-1.5 rounded-full bg-success-500 animate-pulse" aria-hidden="true" />
        En curso · {room.questionNumber}/{room.questionCount}
      </span>
    );
  }
  if (room.status === 'preparing') {
    return (
      <span className="chip shrink-0 border-sky-600 bg-sky-500/10 text-sky-300 text-[10px]">
        <Sparkles className="w-3 h-3" /> La IA la está escribiendo
      </span>
    );
  }
  if (room.startAt && countdown !== null) {
    return (
      <span
        className="chip shrink-0 border-ember-600 bg-ember-500/10 text-ember-300 text-[10px] tabular-nums"
        title={`Empieza a las ${localClockOf(room.startAt)}`}
      >
        <Clock className="w-3 h-3" />
        {countdown > 60 * 60 * 1000 ? `A las ${localClockOf(room.startAt)}` : `Empieza en ${formatCountdown(countdown)}`}
      </span>
    );
  }
  return null;
};
